import axios from "axios";
import { unauthorizedInterceptors } from "../utilities/functions";

// Configure the axios instance
const mediaPlanAPI = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}/mediaplan`,
  headers: {
    "Content-Type": "application/json",
  },
});

unauthorizedInterceptors(mediaPlanAPI);
const setAuthHeader = () => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    mediaPlanAPI.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }
};

// --- GET cache & de-duplication (prevent duplicate requests) ---
const mediaplanCache = new Map();
const mediaplanInFlight = new Map();
const MEDIAPLAN_TTL_MS = 2 * 60 * 1000; // 2 minutes

export const clearMediaplanCache = () => {
  mediaplanCache.clear();
  console.log("[Cache] Mediaplan cache cleared.");
};

const cachedMediaplanGet = async (url, params = {}, ttlMs = MEDIAPLAN_TTL_MS) => {
  setAuthHeader();
  
  const queryString = typeof params === 'object' && Object.keys(params).length > 0 
    ? new URLSearchParams(params).toString() 
    : '';
  const fullUrl = queryString ? `${url}?${queryString}` : url;
  const key = `GET ${fullUrl}`;
  const now = Date.now();

  const cached = mediaplanCache.get(key);
  if (cached && now - cached.time < ttlMs) {
    return cached.response;
  }

  if (mediaplanInFlight.has(key)) {
    return mediaplanInFlight.get(key);
  }

  const requestPromise = mediaPlanAPI
    .get(url, Object.keys(params).length > 0 ? { params } : undefined)
    .then((response) => {
      mediaplanCache.set(key, { response, time: Date.now() });
      mediaplanInFlight.delete(key);
      return response;
    })
    .catch((err) => {
      mediaplanInFlight.delete(key);
      throw err;
    });

  mediaplanInFlight.set(key, requestPromise);
  return requestPromise;
};


export const getMediaPlansList = async (
  clientIds = [],
  page = 1,
  pageSize = 50,
  viewMode = "list",
  startDate = null,
  endDate = null,
  filters = {}
) => {
  setAuthHeader();
  const params = new URLSearchParams();

  // --- Clients
  if (Array.isArray(clientIds) && clientIds.length > 0) {
    clientIds.forEach((id) => params.append("client_ids[]", id));
  } else {
    params.append("client_ids[]", "all");
  }

  // --- Pagination & View
  params.append("page", page);
  params.append("page_size", pageSize);
  params.append("view_mode", viewMode);

  // --- Date Range
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);

  // --- Campaign Codes
  if (filters.campaignCodes?.length) {
    filters.campaignCodes.forEach((code) =>
      params.append("campaign_codes[]", code)
    );
  }

  // --- Product Variations
  if (filters.productVariations?.length) {
    filters.productVariations.forEach((pv) =>
      params.append("product_variations[]", pv)
    );
  }

  // --- Brands
  if (filters.brands?.length) {
    filters.brands.forEach((brandId) =>
      params.append("brand_ids[]", brandId)
    );
  }

  // --- Strategies
  if (filters.strategies?.length) {
    filters.strategies.forEach((s) =>
      params.append("strategy", s)
    );
  }

  // --- Offer Codifications
  if (filters.offerCodifications?.length) {
    filters.offerCodifications.forEach((code) =>
      params.append("offer_codifications[]", code)
    );
  }

  return await mediaPlanAPI.get(`api/media-plans-list/?${params.toString()}`);
};


// Get all Media Plans (basic CRUD)
export const getMediaPlans = async () => {
  setAuthHeader();
  return await mediaPlanAPI.get("api/media-plans/");
};

// Get lightweight Media Plans (optimized for list selection)
export const getLightweightMediaPlans = async (clientIds = [], brandIds = []) => {
  setAuthHeader();
  const params = new URLSearchParams();
  
  // Add client filters if provided
  if (Array.isArray(clientIds) && clientIds.length > 0) {
    clientIds.forEach((id) => params.append("client_ids[]", id));
  }
  
  // Add brand filters if provided
  if (Array.isArray(brandIds) && brandIds.length > 0) {
    brandIds.forEach((id) => params.append("brand_ids[]", id));
  }
  
  return await mediaPlanAPI.get(`api/lightweight-media-plans/?${params.toString()}`);
};

// Get a specific Media Plan by ID
export const getMediaPlanById = async (id) => {
  setAuthHeader();
  return await mediaPlanAPI.get(`api/media-plans/${id}/`);
};

// Create a new Media Plan
export const createMediaPlan = async (data) => {
  setAuthHeader();
  return await mediaPlanAPI.post("api/media-plans/", data);
};

// Update an existing Media Plan by ID
export const updateMediaPlan = async (id, data) => {
  setAuthHeader();
  return await mediaPlanAPI.put(`api/media-plans/${id}/`, data);
};

// Delete a Media Plan by ID
export const deleteMediaPlan = async (id) => {
  setAuthHeader();
  return await mediaPlanAPI.delete(`api/media-plans/${id}/`);
};

// Get all Strategies
export const getStrategies = async () => {
  setAuthHeader();
  return await mediaPlanAPI.get("api/strategies/");
};

export const getProducts = async () => {
  setAuthHeader();
  return await mediaPlanAPI.get("api/products/");
};

export const createStrategy = async (strategyData) => {
  setAuthHeader();
  return await mediaPlanAPI.post("api/strategies/", strategyData);
};

// === Item Estimation API Functions ===

// Get the list of Item Estimations, optionally filtered by Media Plan ID
export const getItemEstimations = async (mediaPlanId = null) => {
  setAuthHeader();
  const endpoint = mediaPlanId
    ? `api/item-estimations/?media_plan_id=${mediaPlanId}`
    : "api/item-estimations/";
  return await mediaPlanAPI.get(endpoint);
};

// Get a specific Item Estimation by ID
export const getItemEstimationById = async (id) => {
  setAuthHeader();
  return await mediaPlanAPI.get(`api/item-estimations/${id}/`);
};

// === Variable Costs API Functions ===

// Get all Variable Costs
export const getAllVariableCosts = async () => {
  setAuthHeader();
  return await mediaPlanAPI.get("api/variable-costs/");
};

// Get Variable Costs by Product ID
export const getVariableCostsByProduct = async (productId) => {
  setAuthHeader();
  return await mediaPlanAPI.get(`api/variable-costs/by-product/?product_id=${productId}`);
};

// Get Variable Costs by Offer ID
export const getVariableCostsByOffer = async (productId) => {
  setAuthHeader();
  return await mediaPlanAPI.get(`api/variable-costs/by-product/?product_id=${productId}`);
};

// Get a specific Variable Cost by ID
export const getVariableCostById = async (id) => {
  setAuthHeader();
  return await mediaPlanAPI.get(`api/variable-costs/${id}/`);
};

// Create a new Variable Cost
export const createVariableCost = async (data) => {
  setAuthHeader();
  return await mediaPlanAPI.post("api/variable-costs/", data);
};

// Update an existing Variable Cost by ID
export const updateVariableCost = async (id, data) => {
  setAuthHeader();
  return await mediaPlanAPI.put(`api/variable-costs/${id}/`, data);
};

// Delete a Variable Cost by ID
export const deleteVariableCost = async (id) => {
  setAuthHeader();
  return await mediaPlanAPI.delete(`api/variable-costs/${id}/`);
};

// Create a new Item Estimation
export const createItemEstimation = async (data) => {
  setAuthHeader();
  return await mediaPlanAPI.post("api/item-estimations/", data);
};

// Update an existing Item Estimation by ID
export const updateItemEstimation = async (id, data) => {
  setAuthHeader();
  return await mediaPlanAPI.put(`api/item-estimations/${id}/`, data);
};

// Delete an Item Estimation by ID
export const deleteItemEstimation = async (id) => {
  setAuthHeader();
  return await mediaPlanAPI.delete(`api/item-estimations/${id}/`);
};

export const getMediaPlansByProductVariation = async (productVariationId) => {
  setAuthHeader();
  return await mediaPlanAPI.get(`api/media-plans/`, {
    params: {
      offer__product_variation: productVariationId,
    },
  });
};

// Fetch Media Plans and Item Estimations by Product Variation
export const getMediaPlansAndItemEstimationsByProductVariation = async (
  productVariationId
) => {
  setAuthHeader();
  return await mediaPlanAPI.get(
    `api/media-plan-item-estimation/by-product-variation/${productVariationId}/`
  );
};
export const getMediaPlansByOffer = async (offerId) => {
  setAuthHeader();
  return await mediaPlanAPI.get(`api/media-plans/`, {
    params: {
      offer: offerId,
    },
  });
};

export const getMediaPlansByOfferAndPrinter = async (offerId, printerId) => {
  setAuthHeader();
  return await mediaPlanAPI.get(`api/media-plans/`, {
    params: {
      offer: offerId,
      printer: printerId,
    },
  });
};

export const makePriorityList = async (clientIds = []) => {
  setAuthHeader();

  const params = new URLSearchParams();

  if (clientIds && clientIds.length > 0) {
    clientIds.forEach((id) => params.append("client_ids[]", id));
  }

  const queryString = params.toString();
  return await mediaPlanAPI.get(`api/media-plans/marketing-priority/?${queryString}`);
};



// CACHED - called from multiple components
export const getLists = async () => {
  return await cachedMediaplanGet("api/lists/");
};


export const getTopLists = async (productId = null) => {
  setAuthHeader();
  const params = productId ? { product_id: productId } : {};
  return await mediaPlanAPI.get("api/top-list/", { params });
};

export const getRankedTopLists = async (productId = null) => {
  setAuthHeader();
  const params = productId ? { product_id: productId } : {};
  return await mediaPlanAPI.get("api/ranked-top-list/", { params });
};

export const createList = async (listData) => {
  setAuthHeader();
  return await mediaPlanAPI.post("api/lists/", listData);
};

export const updateList = async (id, listData) => {
  setAuthHeader();
  return await mediaPlanAPI.put(`api/lists/${id}/`, listData);
};

export const deleteList = async (id) => {
  setAuthHeader();
  return await mediaPlanAPI.delete(`api/lists/${id}/`);
};

export const getSegments = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams(filters);
  return await mediaPlanAPI.get(`api/segments/?${params.toString()}`);
};

export const createSegment = async (segmentData) => {
  setAuthHeader();
  return await mediaPlanAPI.post("api/segments/", segmentData);
};

export const updateSegment = async (id, segmentData) => {
  setAuthHeader();
  return await mediaPlanAPI.put(`api/segments/${id}/`, segmentData);
};

export const deleteSegment = async (id) => {
  setAuthHeader();
  return await mediaPlanAPI.delete(`api/segments/${id}/`);
};

export const getListById = async (id) => {
  setAuthHeader();
  return await mediaPlanAPI.get(`api/lists/${id}/`);
};

export const getMediaPlanListSegments = async () => {
  setAuthHeader();
  return await mediaPlanAPI.get("api/media-plan-list-segments/");
};

export const getDetailedMediaPlanListSegments = async (mediaPlanId = null) => {
  setAuthHeader();
  let url = "api/media-plan-list-segments/detailed/";
  if (mediaPlanId) {
    url += `?media_plan=${mediaPlanId}`;
  }
  return await mediaPlanAPI.get(url);
};

export const createMediaPlanListSegment = async (listSegmentData) => {
  setAuthHeader();
  return await mediaPlanAPI.post(
    "api/media-plan-list-segments/",
    listSegmentData
  );
};

export const bulkCreateMediaPlanListSegments = async (mediaPlanId, listSegments) => {
  setAuthHeader();
  return await mediaPlanAPI.post(
    "api/media-plan-list-segments/bulk-create/",
    {
      media_plan_id: mediaPlanId,
      list_segments: listSegments
    }
  );
};

export const updateMediaPlanListSegment = async (id, listSegmentData) => {
  setAuthHeader();
  return await mediaPlanAPI.put(
    `api/media-plan-list-segments/${id}/`,
    listSegmentData
  );
};

export const deleteMediaPlanListSegment = async (id) => {
  setAuthHeader();
  return await mediaPlanAPI.delete(`api/media-plan-list-segments/${id}/`);
};

export const getMediaPlanListSegmentById = async (id) => {
  setAuthHeader();
  return await mediaPlanAPI.get(`api/media-plan-list-segments/${id}/`);
};

export const getProductPlanningResults = async (params = {}) => {
  setAuthHeader();

  // Build query string from params
  const query = new URLSearchParams();

  if (params.client_ids && params.client_ids.length > 0) {
    params.client_ids.forEach((id) => query.append("client_ids[]", id));
  }

  if (params.start_date) {
    query.append("start_date", params.start_date);
  }

  if (params.end_date) {
    query.append("end_date", params.end_date);
  }

  return await mediaPlanAPI.get(`api/product-planning/?${query.toString()}`);
};

export const getProductOrderPrediction = async (clientIds = []) => {
  setAuthHeader();

  const params = new URLSearchParams();

  if (clientIds && clientIds.length > 0) {
    clientIds.forEach((id) => params.append("client_ids[]", id));
  }
  const queryString = params.toString();
  return await mediaPlanAPI.get(`api/product-order-prediction/?${queryString}`);
};

export const getMediaPlansByDate = async (date, brand_id) => {
  setAuthHeader();
  return await mediaPlanAPI.get(`api/media-plans/by-date/`, {
    params: {
      date,
      brand: brand_id,
    },
  });
};

export const getMediaPlansByWeek = async (startDate, endDate, brand_id) => {
  setAuthHeader();
  return await mediaPlanAPI.get(`api/media-plans/by-week/`, {
    params: {
      start_date: startDate,
      end_date: endDate,
      brand: brand_id,
    },
  });
};

export const getProductRelatedCampaignCount = async (productId) => {
  setAuthHeader();
  return await mediaPlanAPI.get(`api/media-plans/by-product-count/`, {
    params: {
      product_id: productId,
    },
  });
};

export const getAllCosts = async () => {
  setAuthHeader();
  return await mediaPlanAPI.get("api/costs/");
};

export const createCost = async (costData) => {
  setAuthHeader();
  return await mediaPlanAPI.post("api/costs/", costData); 
};

export const getProductVariationOrderPrediction = async (variationIds = []) => {
  setAuthHeader();
  
  const params = new URLSearchParams();
  if (variationIds && variationIds.length > 0) {
    variationIds.forEach((id) => params.append("variation_ids[]", id));
  }
  
  const queryString = params.toString();
  return await mediaPlanAPI.get(`api/product-variation-order-prediction/?${queryString}`);
};

export const getDailyProductOrderPrediction = async (variationIds = [], startDate, endDate, newCampaignData = null) => {
  setAuthHeader();
  
  const params = new URLSearchParams();
  if (variationIds && variationIds.length > 0) {
    variationIds.forEach((id) => params.append("variation_ids[]", id));
  }
  
  if (startDate) {
    params.append("start_date", startDate.format('YYYY-MM-DD'));
  }
  
  if (endDate) {
    params.append("end_date", endDate.format('YYYY-MM-DD'));
  }
  
  // Add new campaign data if provided
  if (newCampaignData) {
    params.append("new_campaign_data", JSON.stringify(newCampaignData));
  }
  
  const queryString = params.toString();
  console.log("DailyProductOrderData:", newCampaignData, variationIds);
  return await mediaPlanAPI.get(`api/daily-product-order-prediction/?${queryString}`);
};

export const getCampaignStockActions = async (campaignData, variationIds = [], startDate = null, endDate = null, options = {}) => {
  setAuthHeader();
  
  // Create URL parameters similar to getDailyProductOrderPrediction
  const params = new URLSearchParams();
  
  // Add variation IDs if provided
  if (variationIds && variationIds.length > 0) {
    variationIds.forEach((id) => params.append("variation_ids[]", id));
  } else if (campaignData && campaignData.variation) {
    // Use variation from campaignData if variationIds not provided
    params.append("variation_ids[]", campaignData.variation);
  }
  
  // Add date range if provided
  if (startDate) {
    params.append("start_date", typeof startDate.format === 'function' ? startDate.format('YYYY-MM-DD') : startDate);
  } else if (campaignData && campaignData.mailDate) {
    // Use mailDate from campaignData if startDate not provided
    params.append("start_date", campaignData.mailDate);
  }
  
  if (endDate) {
    params.append("end_date", typeof endDate.format === 'function' ? endDate.format('YYYY-MM-DD') : endDate);
  } else if (campaignData && campaignData.mailDate) {
    // Default to mailDate + 100 days if endDate not provided
    const endDateValue = new Date(campaignData.mailDate);
    endDateValue.setDate(endDateValue.getDate() + 100);
    params.append("end_date", endDateValue.toISOString().split('T')[0]);
  }
  
  // Add campaign data
  if (campaignData) {
    params.append("new_campaign_data", JSON.stringify([campaignData]));
  }
  
  const queryString = params.toString();
  console.log("CampaignStockActionsData:", campaignData, variationIds, startDate, endDate);
  
  // Use GET request with query parameters instead of POST with body
  return await mediaPlanAPI.get(`api/campaign-stock-actions/?${queryString}`, options);
};

export const getLatestCostBySupplier = async (supplierId, costType, brandId = null) => {
  setAuthHeader();
  const params = new URLSearchParams();
  params.append('supplier_id', supplierId);
  params.append('cost_type', costType);
  
  if (brandId) {
    params.append('brand_id', brandId);
  }
  
  return await mediaPlanAPI.get(`api/costs/latest-by-supplier/?${params.toString()}`);
};

export const getStockEvolution = async (startDate, endDate, clientIds = [], excludeDelivery = true, targetDate = null) => {
  setAuthHeader();
  
  const params = new URLSearchParams();
  
  if (startDate) {
    params.append("start_date", typeof startDate.format === 'function' ? startDate.format('YYYY-MM-DD') : startDate);
  }
  
  if (endDate) {
    params.append("end_date", typeof endDate.format === 'function' ? endDate.format('YYYY-MM-DD') : endDate);
  }
  
  if (clientIds && clientIds.length > 0) {
    clientIds.forEach((id) => params.append("client_ids[]", id));
  }
  
  // Add exclude_delivery parameter
  params.append("exclude_delivery", excludeDelivery);
  
  // Add target_date parameter if provided
  if (targetDate) {
    params.append("target_date", typeof targetDate.format === 'function' ? targetDate.format('YYYY-MM-DD') : targetDate);
  }
  
  return await mediaPlanAPI.get(`api/stock-evolution/?${params.toString()}`);
};

export const getCashFlowProducts = async (filters = {}) => {
  setAuthHeader();
  
  const {
    brandIds = [],
    productCategoryIds = [],
    campaignIds = [],
    startDate,
    endDate,
    dateRange = []
  } = filters;
  
  const params = new URLSearchParams();
  
  if (brandIds && brandIds.length > 0) {
    brandIds.forEach((id) => params.append("brand_ids[]", id));
  }
  
  if (productCategoryIds && productCategoryIds.length > 0) {
    productCategoryIds.forEach((id) => params.append("product_category_ids[]", id));
  }
  
  if (campaignIds && campaignIds.length > 0) {
    campaignIds.forEach((id) => params.append("media_plan_ids[]", id));
  }
  
  // Use dateRange if available, otherwise fall back to startDate/endDate
  if (dateRange && dateRange.length === 2) {
    params.append("start_date", typeof dateRange[0].format === 'function' ? dateRange[0].format('YYYY-MM-DD') : dateRange[0]);
    params.append("end_date", typeof dateRange[1].format === 'function' ? dateRange[1].format('YYYY-MM-DD') : dateRange[1]);
  } else {
    if (startDate) {
      params.append("start_date", typeof startDate.format === 'function' ? startDate.format('YYYY-MM-DD') : startDate);
    }
    
    if (endDate) {
      params.append("end_date", typeof endDate.format === 'function' ? endDate.format('YYYY-MM-DD') : endDate);
    }
  }
  
  return await mediaPlanAPI.get(`api/cash-flow-products/?${params.toString()}`);
};

export const getCashFlowProductCategories = async (filters = {}) => {
  setAuthHeader();
  
  const {
    brandIds = [],
    productIds = [],
    campaignIds = [],
    startDate,
    endDate,
    dateRange = []
  } = filters;
  
  const params = new URLSearchParams();
  
  if (brandIds && brandIds.length > 0) {
    brandIds.forEach((id) => params.append("brand_ids[]", id));
  }
  
  if (productIds && productIds.length > 0) {
    productIds.forEach((id) => params.append("product_ids[]", id));
  }
  
  if (campaignIds && campaignIds.length > 0) {
    campaignIds.forEach((id) => params.append("media_plan_ids[]", id));
  }
  
  // Use dateRange if available, otherwise fall back to startDate/endDate
  if (dateRange && dateRange.length === 2) {
    params.append("start_date", typeof dateRange[0].format === 'function' ? dateRange[0].format('YYYY-MM-DD') : dateRange[0]);
    params.append("end_date", typeof dateRange[1].format === 'function' ? dateRange[1].format('YYYY-MM-DD') : dateRange[1]);
  } else {
    if (startDate) {
      params.append("start_date", typeof startDate.format === 'function' ? startDate.format('YYYY-MM-DD') : startDate);
    }
    
    if (endDate) {
      params.append("end_date", typeof endDate.format === 'function' ? endDate.format('YYYY-MM-DD') : endDate);
    }
  }
  
  return await mediaPlanAPI.get(`api/cash-flow-product-categories/?${params.toString()}`);
};

export const getCashFlowCampaigns = async (filters = {}) => {
  setAuthHeader();
  
  const {
    brandIds = [],
    productIds = [],
    productCategoryIds = [],
    startDate,
    endDate,
    dateRange = []
  } = filters;
  
  const params = new URLSearchParams();
  
  if (brandIds && brandIds.length > 0) {
    brandIds.forEach((id) => params.append("brand_ids[]", id));
  }
  
  if (productIds && productIds.length > 0) {
    productIds.forEach((id) => params.append("product_ids[]", id));
  }
  
  if (productCategoryIds && productCategoryIds.length > 0) {
    productCategoryIds.forEach((id) => params.append("product_category_ids[]", id));
  }
  
  // Use dateRange if available, otherwise fall back to startDate/endDate
  if (dateRange && dateRange.length === 2) {
    params.append("start_date", typeof dateRange[0].format === 'function' ? dateRange[0].format('YYYY-MM-DD') : dateRange[0]);
    params.append("end_date", typeof dateRange[1].format === 'function' ? dateRange[1].format('YYYY-MM-DD') : dateRange[1]);
  } else {
    if (startDate) {
      params.append("start_date", typeof startDate.format === 'function' ? startDate.format('YYYY-MM-DD') : startDate);
    }
    
    if (endDate) {
      params.append("end_date", typeof endDate.format === 'function' ? endDate.format('YYYY-MM-DD') : endDate);
    }
  }
  
  return await mediaPlanAPI.get(`api/cash-flow-campaigns/?${params.toString()}`);
};

export const getEstimatedCashFlow = async (filters = {}) => {
  setAuthHeader();
  
  const {
    startDate,
    endDate,
    dateRange = [],
    brandIds = [],
    productIds = [],
    productCategoryIds = [],
    campaignIds = [],
    timePeriod = 'daily'
  } = filters;
  
  const params = new URLSearchParams();
  
  // Use dateRange if available, otherwise fall back to startDate/endDate
  if (dateRange && dateRange.length === 2) {
    params.append("start_date", typeof dateRange[0].format === 'function' ? dateRange[0].format('YYYY-MM-DD') : dateRange[0]);
    params.append("end_date", typeof dateRange[1].format === 'function' ? dateRange[1].format('YYYY-MM-DD') : dateRange[1]);
  } else {
    if (startDate) {
      params.append("start_date", typeof startDate.format === 'function' ? startDate.format('YYYY-MM-DD') : startDate);
    }
    
    if (endDate) {
      params.append("end_date", typeof endDate.format === 'function' ? endDate.format('YYYY-MM-DD') : endDate);
    }
  }
  
  if (brandIds && brandIds.length > 0) {
    brandIds.forEach((id) => params.append("brand_ids[]", id));
  }
  
  if (productIds && productIds.length > 0) {
    productIds.forEach((id) => params.append("product_ids[]", id));
  }
  
  if (productCategoryIds && productCategoryIds.length > 0) {
    productCategoryIds.forEach((id) => params.append("product_category_ids[]", id));
  }
  
  if (campaignIds && campaignIds.length > 0) {
    campaignIds.forEach((id) => params.append("media_plan_ids[]", id));
  }
  
  params.append("time_period", timePeriod);
  
  return await mediaPlanAPI.get(`api/estimated-cash-flow/?${params.toString()}`);
};

export const fetchListOrderList = async (clientIds = [], page = 1, pageSize = 50) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (Array.isArray(clientIds) && clientIds.length > 0) {
    clientIds.forEach((id) => params.append("client_ids[]", id));
  } else {
    params.append("client_ids[]", "all");
  }

  params.append("page", page);
  params.append("page_size", pageSize);

  return await mediaPlanAPI.get(`api/list-orders/?${params.toString()}`);
};

export const deleteListOrder = async (id) => {
  setAuthHeader();
  return await mediaPlanAPI.delete(`api/list-orders/${id}/`);
};
